import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { MapPin, ArrowRight, ChevronDown } from 'lucide-react'
import { PROPERTIES } from '../constants'
import Container from './Container'
import AnimatedSection from './AnimatedSection'
import { staggerContainerVariants, staggerItemVariants } from '../utils/animations'

const PropertyGrid: React.FC = () => {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id)
  }

  const renderList = (items: string[]) => (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex items-start text-sm text-text-secondary md:text-base">
          <span className="mr-2 text-accent">•</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )

  return (
    <section id="proyectos" className="bg-surface py-24 md:py-32">
      <Container size="xl">
        {/* Section Header */}
        <AnimatedSection className="mx-auto mb-16 max-w-3xl text-center">
          {/* Section Label */}
          <span className="section-label mb-4 block">Portafolio</span>
          <h2 className="accent-line-center mb-6 text-3xl font-bold text-primary md:text-4xl lg:text-5xl">
            Proyectos destacados
          </h2>
          <p className="text-lg text-text-secondary">
            Explora opciones residenciales y de inversión en destinos con alta demanda.
          </p>
        </AnimatedSection>

        {/* Properties Grid - 2 columns on desktop, 1 on mobile */}
        <motion.div
          className="grid grid-cols-1 gap-8 md:grid-cols-2"
          variants={staggerContainerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
        >
          {PROPERTIES.map((property) => (
            <motion.div
              key={property.id}
              variants={staggerItemVariants}
              className="card-hover group overflow-hidden rounded-xl border border-border-light bg-background"
            >
              {/* Property Image */}
              <div className="image-zoom-container relative h-64">
                <img
                  src={property.image}
                  alt={property.name}
                  className="image-zoom h-full w-full object-cover"
                />
                {/* Subtle gradient overlay on image */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>

              {/* Property Content */}
              <div className="p-6 md:p-8">
                {/* Property Name */}
                <h3 className="mb-2 text-xl font-bold text-primary md:text-2xl">{property.name}</h3>

                {/* Location */}
                <div className="mb-4 flex items-center text-text-muted">
                  <MapPin className="mr-1 h-4 w-4" aria-hidden={true} />
                  <span className="text-sm">{property.location}</span>
                </div>

                {/* Price & Reserve Box */}
                <div className="mb-5 rounded-xl bg-secondary-light p-4 md:p-5">
                  <p className="text-lg font-semibold text-accent">
                    Desde US$ {property.priceFrom}
                  </p>
                  <p className="mt-2 text-sm text-text-secondary md:text-base">
                    Reserva: US$ {property.reserve}
                  </p>
                </div>

                {/* Toggle Button */}
                <button
                  onClick={() => toggleExpand(property.id)}
                  className="group mb-4 flex w-full items-center justify-center gap-2 rounded-lg border border-border-light px-4 py-3 text-sm font-semibold text-primary transition-all duration-300 hover:border-accent hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                  aria-expanded={expandedId === property.id}
                  aria-controls={`details-${property.id}`}
                >
                  <span>Ver detalles</span>
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-300 ${
                      expandedId === property.id ? 'rotate-180' : ''
                    }`}
                    aria-hidden={true}
                  />
                </button>

                {/* Expandable Details Section */}
                <AnimatePresence initial={false}>
                  {expandedId === property.id && (
                    <motion.div
                      id={`details-${property.id}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="mb-6 space-y-5 pt-2">
                        <div>
                          <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-primary">
                            Amenidades
                          </h4>
                          {renderList(property.amenities)}
                        </div>

                        <div>
                          <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-primary">
                            Tipos de apartamentos
                          </h4>
                          {renderList(property.apartmentTypes)}
                        </div>

                        <div>
                          <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-primary">
                            Baños
                          </h4>
                          {renderList(property.bathrooms)}
                        </div>

                        {property.parking && property.parking.length > 0 && (
                          <div>
                            <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-primary">
                              Estacionamientos
                            </h4>
                            {renderList(property.parking)}
                          </div>
                        )}
                      </div>

                      {/* CTA Button - Only visible when expanded */}
                      <a
                        href="#contacto"
                        className="group/btn inline-flex w-full items-center justify-center rounded-lg border-2 border-primary px-6 py-3 font-semibold text-primary transition-all duration-300 hover:bg-primary hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
                      >
                        Solicitar info
                        <ArrowRight className="btn-arrow ml-2 h-4 w-4" aria-hidden={true} />
                      </a>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Under-grid CTA */}
        <AnimatedSection delay={0.3}>
          <div className="mt-16 rounded-2xl bg-secondary-light p-8 text-center md:p-12">
            <h3 className="mb-4 text-2xl font-bold text-primary md:text-3xl">
              ¿Quieres disponibilidad y precios actualizados?
            </h3>
            <p className="mx-auto mb-8 max-w-2xl text-text-secondary">
              Agenda una llamada o déjanos tus datos y te enviamos opciones alineadas a tu
              presupuesto.
            </p>
            <a
              href="#contacto"
              className="group inline-flex items-center justify-center rounded-lg bg-accent px-8 py-4 font-semibold text-white transition-all duration-300 hover:bg-accent-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              Quiero disponibilidad y precios
              <ArrowRight className="btn-arrow ml-2 h-5 w-5" aria-hidden={true} />
            </a>
          </div>
        </AnimatedSection>
      </Container>
    </section>
  )
}

export default PropertyGrid
